import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Star, CheckCircle2, Pill, Heart, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-1" data-testid="share-story-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        aria-label={`${n} stars`}
        className="hover:scale-110 transition-transform"
      >
        <Star className={`w-7 h-7 ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      </button>
    ))}
  </div>
);

export default function ShareYourStoryPage() {
  const { userId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    rating: 5,
    headline: "",
    body: "",
    display_name_choice: "first_initial",
    city_state: "",
    weight_lost_lbs: "",
    duration_weeks: "",
    medication: "",
    release_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    API.get(`/share-your-story/${userId}/${token}`)
      .then(({ data }) => {
        if (!alive) return;
        setMeta(data);
        if (data.already_submitted) setSubmitted(true);
      })
      .catch((e) => alive && setError(e?.response?.data?.detail || "Invalid or expired link"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [userId, token]);

  const submit = async () => {
    if (!form.release_consent) {
      toast.error("Please confirm the media release to continue");
      return;
    }
    if ((form.body || "").trim().length < 20) {
      toast.error("Story must be at least 20 characters");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        weight_lost_lbs: form.weight_lost_lbs === "" ? null : parseInt(form.weight_lost_lbs, 10),
        duration_weeks: form.duration_weeks === "" ? null : parseInt(form.duration_weeks, 10),
      };
      const { data } = await API.post(`/share-your-story/${userId}/${token}`, payload);
      setSubmitted(true);
      toast.success(`Thank you ${data.display_name}!`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <CenteredMessage icon={<Pill className="w-8 h-8 text-purple-500 animate-spin" />} title="Loading…" />;
  }
  if (error) {
    return (
      <CenteredMessage
        icon={<AlertCircle className="w-8 h-8 text-rose-500" />}
        title="This link doesn't work"
        subtitle={error}
        testId="share-story-error"
      />
    );
  }
  if (submitted) {
    return (
      <CenteredMessage
        icon={<CheckCircle2 className="w-10 h-10 text-emerald-500" />}
        title="Thank you!"
        subtitle="Your story is being reviewed by our team. Thanks for sharing — we&apos;ll be in touch if we ever add a curated stories page in the future."
        testId="share-story-submitted"
      />
    );
  }

  const first = meta?.first_name || "there";

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white" data-testid="share-your-story-page">
      <nav className="max-w-3xl mx-auto px-4 sm:px-6 py-5">
        <Link to="/" className="inline-flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
          <span className="font-bold text-xl text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
        </Link>
      </nav>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pb-16">
        <header className="text-center mb-8">
          <div className="inline-flex w-14 h-14 rounded-full bg-purple-100 items-center justify-center mb-3">
            <Heart className="w-7 h-7 text-purple-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Hey {first}, share your story.
          </h1>
          <p className="mt-2 text-slate-600 max-w-md mx-auto">
            90 seconds. Your story could be the reason someone else takes the first step. We&apos;ll never share it without your sign-off below.
          </p>
        </header>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-5">
          <div>
            <Label className="text-sm font-semibold mb-2 block">How would you rate GLPFlo overall?</Label>
            <StarPicker value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
          </div>

          <div>
            <Label className="text-sm font-semibold">A short headline (optional)</Label>
            <Input
              value={form.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })}
              placeholder='"Finally feeling like myself again."'
              maxLength={120}
              data-testid="share-story-headline"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold">Your story <span className="text-rose-500">*</span></Label>
            <Textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={5}
              placeholder="What changed for you? How did it feel? Anything specific that made GLPFlo work for you?"
              maxLength={2000}
              data-testid="share-story-body"
            />
            <p className="text-[11px] text-slate-400 mt-1">{form.body.length} / 2000</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-semibold">Lbs lost (optional)</Label>
              <Input
                type="number"
                value={form.weight_lost_lbs}
                onChange={(e) => setForm({ ...form, weight_lost_lbs: e.target.value })}
                placeholder="28"
                data-testid="share-story-lbs"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Over how many weeks?</Label>
              <Input
                type="number"
                value={form.duration_weeks}
                onChange={(e) => setForm({ ...form, duration_weeks: e.target.value })}
                placeholder="12"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-semibold">Medication</Label>
              <Input
                value={form.medication}
                onChange={(e) => setForm({ ...form, medication: e.target.value })}
                placeholder="Semaglutide"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">City, State</Label>
              <Input
                value={form.city_state}
                onChange={(e) => setForm({ ...form, city_state: e.target.value })}
                placeholder="Austin, TX"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold mb-2 block">How should we credit you?</Label>
            <Select
              value={form.display_name_choice}
              onValueChange={(v) => setForm({ ...form, display_name_choice: v })}
            >
              <SelectTrigger data-testid="share-story-name-choice"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="first_initial">First name + last initial (e.g. Sarah M.) — recommended</SelectItem>
                <SelectItem value="full">My full name</SelectItem>
                <SelectItem value="anonymous">Anonymous</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-4">
            <div className="flex items-start gap-2.5">
              <Checkbox
                id="release"
                checked={form.release_consent}
                onCheckedChange={(v) => setForm({ ...form, release_consent: !!v })}
                data-testid="share-story-consent"
              />
              <label htmlFor="release" className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed cursor-pointer">
                <b>Media release:</b> I give GLPFlo permission to share my story (and any photos I&apos;ve provided) on glpflo.org, in marketing materials, and on social media. I understand I can withdraw permission any time by emailing <a href="mailto:customer.service@glpflo.com" className="text-purple-600 underline">customer.service@glpflo.com</a>. I confirm the story is my own and accurate.
              </label>
            </div>
          </div>

          <Button
            onClick={submit}
            disabled={submitting || !form.release_consent || (form.body || "").trim().length < 20}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            size="lg"
            data-testid="share-story-submit"
          >
            {submitting ? "Submitting…" : "Submit my story"}
          </Button>
          <p className="text-[11px] text-slate-400 text-center">
            We review every submission before it goes live. We may lightly edit for clarity but won&apos;t change the meaning.
          </p>
        </div>
      </div>
    </div>
  );
}

function CenteredMessage({ icon, title, subtitle, testId }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid={testId}>
      <div className="text-center max-w-md">
        <div className="inline-flex w-16 h-16 rounded-full bg-white shadow-sm items-center justify-center mb-4">{icon}</div>
        <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
        {subtitle && <p className="mt-2 text-slate-600">{subtitle}</p>}
        <Link to="/" className="inline-block mt-4 text-sm font-semibold text-purple-600 hover:text-purple-800">← Back to GLPFlo</Link>
      </div>
    </div>
  );
}
